
import { BaseService } from '../../../http/BaseHttpService';
import {
    CreateNexusChatSessionRequest,
    IChatSessionWriteService,
    UpdateChatSessionRequest,
} from '../../../chat/interfaces/IChatSessionWriteService';
import { ChatSessionLoadResult } from '../../../chat/interfaces/IChatSessionsService';
import { mapNexusConversationDtoToModel } from '../mappers/NexusConversationMapper';
import { buildNexusUrl } from '../../../../features/utils/buildNexusUrl';
import { ChatProviderType } from '../../../../domain/chat/models/ChatProviderType';
import { NexusConversationDto } from '../dtos/responses/NexusConversationDto';

export class NexusConversationWriteService extends BaseService implements IChatSessionWriteService {
    public readonly providerType = ChatProviderType.Nexus;
    private readonly subscriptionKey = process.env.REACT_APP_NEXUS_APIM_SUBSCRIPTION_KEY ?? '';
    private readonly subKeyHeader = process.env.REACT_APP_NEXUS_APIM_KEY_HEADER_NAME ?? '';

    constructor(private readonly baseUrl: string) {
        super();
    }

    public async createChatAsync(
        request: CreateNexusChatSessionRequest,
        accessToken: string,
    ): Promise<ChatSessionLoadResult> {
        const response = await this.getResponseAsync<NexusConversationDto>(
            {
                url: buildNexusUrl(this.baseUrl, 'conversations'),
                method: 'POST',
                body: {
                    agentId: request.agentId,
                    ...(request.title != null && { title: request.title }),
                    ...(request.metadata != null && { metadata: request.metadata }),
                    ...(request.systemPrompt != null && { systemPrompt: request.systemPrompt }),
                    ...(request.ttlSeconds != null && { ttlSeconds: request.ttlSeconds }),
                    enableCodeInterpreter: request.enableCodeInterpreter ?? false,
                    enableFileSearch: request.enableFileSearch ?? false,
                },
                headers: {
                    [this.subKeyHeader]: this.subscriptionKey,
                },
            },
            accessToken,
        );

        return mapNexusConversationDtoToModel(response);
    }

    public async deleteChatAsync(chatId: string, accessToken: string): Promise<void> {
        await this.getResponseAsync<void>(
            {
                url: buildNexusUrl(this.baseUrl, `conversations/${encodeURIComponent(chatId)}`),
                method: 'DELETE',
                headers: {
                    [this.subKeyHeader]: this.subscriptionKey,
                },
            },
            accessToken,
        );
    }

    public async hardDeleteChatAsync(chatId: string, accessToken: string): Promise<void> {
        await this.getResponseAsync<void>(
            {
                url: buildNexusUrl(this.baseUrl, `conversations/${encodeURIComponent(chatId)}/hard`),
                method: 'DELETE',
                headers: {
                    [this.subKeyHeader]: this.subscriptionKey,
                },
            },
            accessToken,
        );
    }

    // handles file upload and deletion
    public async updateChatAsync(
        chatId: string,
        request: UpdateChatSessionRequest,
        accessToken: string,
    ): Promise<ChatSessionLoadResult> {
        if (request.providerType !== ChatProviderType.Nexus) {
            throw new Error('NexusConversationWriteService received a non-Nexus update request.');
        }

        const formData = new FormData();

        if (request.title !== undefined && request.title !== null) {
            formData.append('title', request.title);
        }

        if (request.metadata !== undefined && request.metadata !== null) {
            formData.append('metadata', JSON.stringify(request.metadata));
        }

        if (request.enableCodeInterpreter !== undefined && request.enableCodeInterpreter !== null) {
            formData.append('enableCodeInterpreter', String(request.enableCodeInterpreter));
        }

        if (request.enableFileSearch !== undefined && request.enableFileSearch !== null) {
            formData.append('enableFileSearch', String(request.enableFileSearch));
        }

        if (request.filesForFileSearch && request.filesForFileSearch.length > 0) {
            for (const file of request.filesForFileSearch) {
                formData.append('filesForFileSearch', file);
            }
        }

        if (request.filesForCodeInterpreter && request.filesForCodeInterpreter.length > 0) {
            for (const file of request.filesForCodeInterpreter) {
                formData.append('filesForCodeInterpreter', file);
            }
        }

        if (request.filesToDeleteForCodeInterpreter && request.filesToDeleteForCodeInterpreter.length > 0) {
            for (const fileName of request.filesToDeleteForCodeInterpreter) {
                formData.append('filesToDeleteForCodeInterpreter', fileName);
            }
        }

        if (request.filesToDeleteForFileSearch && request.filesToDeleteForFileSearch.length > 0) {
            for (const fileName of request.filesToDeleteForFileSearch) {
                formData.append('filesToDeleteForFileSearch', fileName);
            }
        }
        const response = await this.getResponseAsync<NexusConversationDto>(
            {
                url: buildNexusUrl(this.baseUrl, `conversations/${chatId}`),
                method: 'PATCH',
                body: formData,
                headers: {
                    [this.subKeyHeader]: this.subscriptionKey,
                },
            },
            accessToken,
        );

        return mapNexusConversationDtoToModel(response);
    }
}
